import { useNavigate } from "react-router-dom"
import { useToast } from './useToast';
import { useLoginTalentMutation } from '../Redux/api/talentApi';
import { useLoginClientMutation } from '../Redux/api/clientApi';
import { useAppDispatch } from '../Redux/store';
import { resetGlobalSlice, setIsAdmin, setIsLoggedIn, setUserId, setUserType } from '../Redux/slices/GlobalSlice';
import moment from 'moment';
import { jwtDecode } from 'jwt-decode';
import { ProfileType } from "../Types/General";
import { useGetAdminCredentialsMutation } from "../Redux/api/adminApi";

interface ILoginProps {    
    user_login: string;
    password: string;
}

interface IJWTToken {
    user:string;
    role: string; 
    permissions?: {_id: string, name: string}[] 
    iat: number;
    exp: number;
}

export const useLogin = (type: ProfileType = ProfileType.Admin) => {
    /* ******************** Hooks ******************** */
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [loginTalent, {isLoading: talenLoginIsLoading}] = useLoginTalentMutation();
    const [loginClient, {isLoading: clientLoginIsLoading}] = useLoginClientMutation();
    const [loginAdmin, {isLoading: adminLoginIsLoading}] = useGetAdminCredentialsMutation();
    /* ******************** Variables ******************** */
    const loginLoading = talenLoginIsLoading || clientLoginIsLoading || adminLoginIsLoading;
    /* ******************** Functions ******************** */
    const decodeToken = (token: string) => {
        const decodedToken = jwtDecode<IJWTToken>(token);
        localStorage.setItem('token', token);
        localStorage.setItem('role', decodedToken.role);
        localStorage.setItem('exp', moment.unix(Number(decodedToken.exp)).utc().toString());
        if (decodedToken.permissions) localStorage.setItem('permissions', JSON.stringify(decodedToken.permissions.map( p => p.name)));
        dispatch(setUserId(decodedToken.user))       
    }

    const handleLoginError = (err: any) => {
        console.log(`Error logging in ${type}`);
        console.log(err);
        let toastText = 'Something went wrong'
        switch (err.status){
            case 404:
                toastText = 'Username or Password Is Incorrect';
                break;
            case 400:
                const message = err.data.message;
                if (message)
                    toastText = message;
                break;
        }
        showToast({
            text: toastText,
            variant: 'error'
        })
    }

    const login = (credentials: ILoginProps) => {
        const redirect = localStorage.getItem('redirect');
        const navURL = redirect ?? '/dashboard';
        if (redirect) {
            localStorage.removeItem('redirect');
        }
        if (type == ProfileType.Admin) {
            loginAdmin({...credentials})
            .unwrap()
            .then(async (res: any) => {
                decodeToken(res.token)
                dispatch(setIsAdmin(true));
                dispatch(setIsLoggedIn(true));
                navigate('/admin/manage/users');
            })
            .catch((err) => handleLoginError(err))
        }
        if (type == ProfileType['Talent']) {
            loginTalent({...credentials})
            .unwrap()
            .then(async (res: any) => {
                decodeToken(res.token)
                dispatch(setUserType('talent'));
                dispatch(setIsLoggedIn(true));
                navigate(navURL);
            })
            .catch((err) => handleLoginError(err))
        }
        if (type == ProfileType['Client']) {
            loginClient({...credentials})
            .unwrap()
            .then(async (res) => {
                decodeToken(res.token)
                dispatch(setUserType('client'))
                dispatch(setIsLoggedIn(true));
                navigate(navURL);
            })
            .catch((err) => handleLoginError(err))
        }
    }
    const logout = (route?: string) => {
        localStorage.removeItem('token');
        navigate('/login');
        dispatch(resetGlobalSlice());
        if (route && !route.includes('login')) localStorage.setItem('redirect', route);
    }
    /* ******************** Effects ******************** */
    /* ******************** JSX ******************** */
    return {
        login,
        loginLoading,
        logout
    }
}